// src/features/chat/elora-chat/EloraChatPopover.tsx
import * as React from 'react';
import { Bot, RotateCcw, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Popover, PopoverTrigger, PopoverContent } from '@shared/ui/primitives/popover';
import { EloraChat } from './EloraChat';

type Props = { flowId: string; workspaceId?: string | null };

const MIN_W = 320, MIN_H = 320, MARGIN = 16;

function EloraChatPopoverInner({ flowId, workspaceId }: Props) {
  const [open, setOpen] = React.useState(false);
  const [runner, setRunner] = React.useState<{ isRunning: boolean; reset(): void } | null>(null);
  const [size, setSize] = React.useState({ width: 520, height: 480 });
  const cleanupRef = React.useRef<(() => void) | null>(null);
  const contentId = React.useId();

  const handleRunnerReady = React.useCallback((r: { isRunning: boolean; reset(): void }) => {
    setRunner(r);
  }, []);

  const clamp = React.useCallback((next: { width: number; height: number }) => {
    if (typeof window === 'undefined') return { width: Math.max(MIN_W, next.width), height: Math.max(MIN_H, next.height) };
    const aw = Math.max(240, window.innerWidth - MARGIN * 2);
    const ah = Math.max(240, window.innerHeight - MARGIN * 2);
    return { width: Math.min(Math.max(next.width, MIN_W), aw), height: Math.min(Math.max(next.height, MIN_H), ah) };
  }, []);

  React.useEffect(() => { if (open) setSize((s) => clamp(s)); }, [open, clamp]);
  React.useEffect(() => () => { cleanupRef.current?.(); }, []);
  React.useEffect(() => { if (!open) cleanupRef.current?.(); }, [open]);
  React.useEffect(() => {
    if (!open || typeof window === 'undefined') return;
    const onResize = () => setSize((s) => clamp(s));
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [open, clamp]);

  const onHandleDown = React.useCallback((e: React.PointerEvent<HTMLButtonElement>) => {
    if (e.button !== 0) return;
    e.preventDefault(); e.stopPropagation();
    cleanupRef.current?.();
    const startX = e.clientX, startY = e.clientY;
    const startW = size.width, startH = size.height;
    const pid = e.pointerId; const tgt = e.currentTarget;
    tgt.setPointerCapture?.(pid);
    document.body.style.userSelect = 'none';
    const onMove = (ev: PointerEvent) => {
      const dx = ev.clientX - startX, dy = ev.clientY - startY;
      setSize(clamp({ width: startW + dx, height: startH + dy }));
    };
    const finish = () => {
      tgt.releasePointerCapture?.(pid);
      document.body.style.userSelect = '';
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', finish);
      window.removeEventListener('pointercancel', finish);
      cleanupRef.current = null;
    };
    cleanupRef.current = finish;
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', finish);
    window.addEventListener('pointercancel', finish);
  }, [size.width, size.height, clamp]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="default"
          className="fixed bottom-4 right-[5.5rem] z-[75] h-11 rounded-full px-4 shadow-lg"
          aria-label="Open Elora chat"
          aria-controls={contentId}
        >
          <Bot className="mr-2 h-4 w-4" />
          Elora
        </Button>
      </PopoverTrigger>

      <PopoverContent
        id={contentId}
        draggable
        align="end"
        className="relative flex max-w-[calc(100vw-2rem)] min-h-[320px] min-w-[320px] flex-col overflow-hidden border bg-card p-0 shadow-xl"
        style={{ width: size.width, height: size.height }}
        closeOnOutsideClick={false}
      >
        <div className="flex items-center justify-between border-b px-3 py-2 text-sm" data-popover-drag-handle>
          <span className="font-medium">Elora</span>
          <div className="flex items-center gap-1">
            <Button type="button" size="sm" variant="ghost" onClick={() => runner?.reset()} disabled={!runner || runner.isRunning} aria-label="Reset Elora session" data-popover-no-drag>
              <RotateCcw className="mr-1 h-3.5 w-3.5" />
              Reset
            </Button>
            <Button type="button" size="icon" variant="ghost" onClick={() => setOpen(false)} aria-label="Close Elora chat" data-popover-no-drag>
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>
        <div className="flex flex-1 min-h-0 w-full overflow-hidden p-3">
          <EloraChat flowId={flowId} workspaceId={workspaceId} onRunnerReady={handleRunnerReady} />
        </div>
        <button
          type="button"
          aria-label="Resize Elora chat"
          className="absolute bottom-2 right-2 flex h-4 w-4 cursor-se-resize items-center justify-center rounded border border-border bg-muted/70 text-muted-foreground transition hover:bg-muted"
          onPointerDown={onHandleDown}
          data-popover-no-drag
        >
          <span className="pointer-events-none h-2 w-2 rotate-45 border-b border-r border-current" />
        </button>
      </PopoverContent>
    </Popover>
  );
}

const EloraChatPopover = React.memo(EloraChatPopoverInner);

export { EloraChatPopover };
export default EloraChatPopover;
